import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'

export interface UseDrawingLayersProps {
  isDrawMode: boolean
  drawingPoints: [number, number][]
  snappedGeometry?: GeoJSON.LineString | null
  currentHoverPoint?: [number, number] | null
  hoverPreviewGeometry?: GeoJSON.LineString | null
  isHoverSnapping?: boolean
}

/**
 * Hook for managing drawing mode visualization layers
 * Handles drawing points, snapped line, and hover preview
 */
export function useDrawingLayers({
  isDrawMode,
  drawingPoints,
  snappedGeometry,
  currentHoverPoint,
  hoverPreviewGeometry,
  isHoverSnapping
}: UseDrawingLayersProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()

  // Update cursor style for drawing mode
  useEffect(() => {
    if (!map.current || !isLoaded) return

    if (isDrawMode) {
      map.current.getCanvas().style.cursor = 'crosshair'
    } else {
      map.current.getCanvas().style.cursor = ''
    }
  }, [map, isLoaded, isDrawMode])

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    // Trust styleLoaded from context - same fix as useMapClickMarker
    // Only wait if styleLoaded is false
    if (!styleLoaded) {
      return
    }

    // Clean up if not in draw mode
    if (!isDrawMode) {
      const existingSources = ['drawing-points', 'drawing-line', 'drawing-preview']
      const existingLayers = ['drawing-points', 'drawing-line', 'drawing-preview']
      
      existingLayers.forEach(layerId => {
        if (map.current?.getLayer(layerId)) {
          map.current.removeLayer(layerId)
        }
      })
      existingSources.forEach(sourceId => {
        if (map.current?.getSource(sourceId)) {
          map.current.removeSource(sourceId)
        }
      })
      return
    }

    try {
      // Drawing points
      if (drawingPoints.length > 0) {
        const pointsData = {
          type: 'FeatureCollection' as const,
          features: drawingPoints.map((point, index) => ({
            type: 'Feature' as const,
            properties: { index },
            geometry: {
              type: 'Point' as const,
              coordinates: point
            }
          }))
        }

        if (!map.current.getSource('drawing-points')) {
          map.current.addSource('drawing-points', {
            type: 'geojson',
            data: pointsData
          })
        } else {
          (map.current.getSource('drawing-points') as mapboxgl.GeoJSONSource).setData(pointsData)
        }

        if (!map.current.getLayer('drawing-points')) {
          map.current.addLayer({
            id: 'drawing-points',
            type: 'circle',
            source: 'drawing-points',
            paint: {
              'circle-color': '#3b82f6',
              'circle-radius': 6,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 2
            }
          })
          map.current.moveLayer('drawing-points')
        }
      } else {
        if (map.current.getLayer('drawing-points')) {
          map.current.removeLayer('drawing-points')
        }
        if (map.current.getSource('drawing-points')) {
          map.current.removeSource('drawing-points')
        }
      }

      // Snapped line
      if (snappedGeometry) {
        if (!map.current.getSource('drawing-line')) {
          map.current.addSource('drawing-line', {
            type: 'geojson',
            data: snappedGeometry
          })
        } else {
          (map.current.getSource('drawing-line') as mapboxgl.GeoJSONSource).setData(snappedGeometry)
        }

        if (!map.current.getLayer('drawing-line')) {
          map.current.addLayer({
            id: 'drawing-line',
            type: 'line',
            source: 'drawing-line',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#f97316',
              'line-width': 4,
              'line-dasharray': [2, 2]
            }
          })
          map.current.moveLayer('drawing-line')
        }
      } else {
        if (map.current.getLayer('drawing-line')) {
          map.current.removeLayer('drawing-line')
        }
        if (map.current.getSource('drawing-line')) {
          map.current.removeSource('drawing-line')
        }
      }

      // Preview line - keep it visible during async operations to prevent flickering
      if (currentHoverPoint && isDrawMode && drawingPoints.length > 0) {
        
        const lastPoint = drawingPoints[drawingPoints.length - 1]
        
        // Always show a line immediately - use snapped geometry if available and matches current hover,
        // otherwise show straight line to current hover point
        // Check if hoverPreviewGeometry ends at currentHoverPoint to ensure it's for the right position
        let previewGeometry: GeoJSON.LineString
        if (hoverPreviewGeometry && hoverPreviewGeometry.coordinates.length > 0) {
          const lastCoord = hoverPreviewGeometry.coordinates[hoverPreviewGeometry.coordinates.length - 1]
          // Use snapped geometry only if it ends at the current hover point (within small tolerance)
          const distance = Math.sqrt(
            Math.pow(lastCoord[0] - currentHoverPoint[0], 2) + 
            Math.pow(lastCoord[1] - currentHoverPoint[1], 2)
          )
          // If the snapped geometry matches the current hover point (within ~0.001 degrees), use it
          if (distance < 0.001) {
            previewGeometry = hoverPreviewGeometry
          } else {
            // Snapped geometry is for a different position, show straight line
            previewGeometry = {
              type: 'LineString' as const,
              coordinates: [lastPoint, currentHoverPoint]
            }
          }
        } else {
          // No snapped geometry yet, show straight line
          previewGeometry = {
            type: 'LineString' as const,
            coordinates: [lastPoint, currentHoverPoint]
          }
        }

        const previewData = {
          type: 'Feature' as const,
          properties: {},
          geometry: previewGeometry
        }

        // Check if we're using the snapped geometry (not the straight line fallback)
        const hasSnappedGeometry = previewGeometry.coordinates.length > 2 // Snapped lines have more than 2 points

        if (map.current.getSource('drawing-preview')) {
          // Update the source data immediately - this keeps the line visible and responsive
          (map.current.getSource('drawing-preview') as mapboxgl.GeoJSONSource).setData(previewData)
          
          if (map.current.getLayer('drawing-preview')) {
            // Update visual properties without removing the layer
            map.current.setPaintProperty('drawing-preview', 'line-color', hasSnappedGeometry ? '#10b981' : '#94a3b8')
            map.current.setPaintProperty('drawing-preview', 'line-dasharray', hasSnappedGeometry ? [0] : [2, 2])
            // Keep opacity high to prevent flickering - only slight reduction during snapping
            map.current.setPaintProperty('drawing-preview', 'line-opacity', isHoverSnapping ? 0.9 : 1.0)
          }
        } else {
          try {
            map.current.addSource('drawing-preview', {
              type: 'geojson',
              data: previewData
            })

            map.current.addLayer({
              id: 'drawing-preview',
              type: 'line',
              source: 'drawing-preview',
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': hasSnappedGeometry ? '#10b981' : '#94a3b8',
                'line-width': 4,
                'line-dasharray': hasSnappedGeometry ? [0] : [2, 2],
                'line-opacity': isHoverSnapping ? 0.9 : 1.0
              }
            })
            map.current.moveLayer('drawing-preview')
          } catch (error) {
          }
        }
      } else {
        // Only remove preview when we're truly not in drawing mode or have no hover point
        // Don't remove during async operations
        if (map.current.getSource('drawing-preview') && !isDrawMode) {
          if (map.current.getLayer('drawing-preview')) {
            map.current.removeLayer('drawing-preview')
          }
          map.current.removeSource('drawing-preview')
        }
      }
    } catch (error) {
    }

    return () => {
      if (map.current) {
        const cleanupLayers = ['drawing-points', 'drawing-line', 'drawing-preview']
        const cleanupSources = ['drawing-points', 'drawing-line', 'drawing-preview']
        
        cleanupLayers.forEach(layerId => {
          if (map.current?.getLayer(layerId)) {
            map.current.removeLayer(layerId)
          }
        })
        cleanupSources.forEach(sourceId => {
          if (map.current?.getSource(sourceId)) {
            map.current.removeSource(sourceId)
          }
        })
      }
    }
  }, [map, isLoaded, styleLoaded, isDrawMode, drawingPoints, snappedGeometry, currentHoverPoint, hoverPreviewGeometry, isHoverSnapping])
}

